import { API, toFullUrl } from '../config/api';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Play, Pause, Download, ShoppingBag, Music, Headphones, ArrowLeft, Loader2, Tag, ShoppingCart, ListPlus } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { ShareButtons } from '../components/ShareButtons';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const TABS = [
  { id: 'all', label: 'ALL', icon: ShoppingBag },
  { id: 'tracks', label: 'TRACKS', icon: Music },
  { id: 'mixtapes', label: 'MIXTAPES', icon: Music },
  { id: 'albums', label: 'ALBUMS', icon: Music },
  { id: 'merch', label: 'MERCH', icon: ShoppingBag },
  { id: 'beats', label: 'BEATS', icon: Headphones },
];

export default function Store() {
  const [tab, setTab] = useState('all');
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [mixtapes, setMixtapes] = useState([]);
  const [merch, setMerch] = useState([]);
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentTrack, isPlaying, playTrack, addToQueue } = usePlayer();
  const { addItem, setIsOpen: setCartOpen, totalItems } = useCart();

  // Purchase dialog
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchaseItem, setPurchaseItem] = useState(null);
  const [purchaseType, setPurchaseType] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedLicense, setSelectedLicense] = useState('basic');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [t, m, b, a] = await Promise.all([
        axios.get(`${API}/tracks`),
        axios.get(`${API}/merch`),
        axios.get(`${API}/beats`),
        axios.get(`${API}/albums`).catch(() => ({ data: [] }))
      ]);
      setTracks(t.data.filter(x => x.purchasable && x.price > 0));
      setMerch(m.data.filter(x => x.purchasable));
      setBeats(b.data.filter(x => (x.license_basic > 0 || x.license_premium > 0 || x.license_exclusive > 0)));
      setAlbums((a.data || []).filter(x => x.is_purchasable && x.price > 0));
    } catch {
      // non-critical
    }
    setLoading(false);
  };

  const getTrackPrice = (t) => t.on_sale && t.sale_price != null ? t.sale_price : t.price;
  const getMerchPrice = (m) => m.on_sale && m.sale_price != null ? m.sale_price : m.price;

  const openPurchase = (item, type) => {
    setPurchaseItem(item);
    setPurchaseType(type);
    setCustomerEmail('');
    setDiscountCode('');
    setDiscountInfo(null);
    setSelectedSize(item.sizes?.[0] || '');
    setSelectedLicense('basic');
    setPurchaseOpen(true);
  };

  const validateDiscount = async () => {
    if (!discountCode.trim()) return;
    setValidatingDiscount(true);
    try {
      const { data } = await axios.post(`${API}/discounts/validate`, { code: discountCode });
      setDiscountInfo(data);
      toast.success(`Discount applied: ${data.discount_percent}% off`);
    } catch { toast.error('Invalid discount code'); setDiscountInfo(null); }
    setValidatingDiscount(false);
  };

  const handleCheckout = async () => {
    if (!customerEmail) return toast.error('Please enter your email');
    setPurchasing(true);
    try {
      let endpoint, body;
      if (purchaseType === 'track') {
        endpoint = `${API}/checkout`;
        const price = getTrackPrice(purchaseItem);
        body = { track_id: purchaseItem.id, email: customerEmail, origin_url: window.location.origin, discount_code: discountCode || undefined };
      } else if (purchaseType === 'merch') {
        endpoint = `${API}/merch/checkout`;
        body = { item_id: purchaseItem.id, email: customerEmail, origin_url: window.location.origin, size: selectedSize || undefined };
      } else if (purchaseType === 'beat') {
        const beat = purchaseItem;
        const priceField = beat.on_sale && beat[`sale_price_${selectedLicense}`] ? `sale_price_${selectedLicense}` : `license_${selectedLicense}`;
        endpoint = `${API}/beats/checkout`;
        body = { beat_id: beat.id, license_type: selectedLicense, email: customerEmail, origin_url: window.location.origin };
      } else if (purchaseType === 'album') {
        endpoint = `${API}/albums/${purchaseItem.id}/checkout`;
        body = { email: customerEmail, origin_url: window.location.origin };
      } else if (purchaseType === 'mixtape') {
        endpoint = `${API}/mixtapes/${purchaseItem.id}/checkout`;
        body = { email: customerEmail, origin_url: window.location.origin };
      }
      const { data } = await axios.post(endpoint, body);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    }
    setPurchasing(false);
  };

  const getPurchasePrice = () => {
    if (!purchaseItem) return 0;
    if (purchaseType === 'track') {
      let p = getTrackPrice(purchaseItem);
      if (discountInfo) p = p * (1 - discountInfo.discount_percent / 100);
      return p;
    }
    if (purchaseType === 'merch') return getMerchPrice(purchaseItem);
    if (purchaseType === 'beat') {
      const beat = purchaseItem;
      if (beat.on_sale && beat[`sale_price_${selectedLicense}`]) return beat[`sale_price_${selectedLicense}`];
      return beat[`license_${selectedLicense}`] || 0;
    }
    if (purchaseType === 'album') {
      const a = purchaseItem;
      return a.on_sale && a.sale_price != null ? a.sale_price : (a.price || 0);
    }
    if (purchaseType === 'mixtape') {
      return purchaseItem.price || 0;
    }
    return 0;
  };

  const hasItems = (tab === 'all' && (tracks.length + merch.length + beats.length + albums.length + mixtapes.length > 0)) ||
    (tab === 'tracks' && tracks.length > 0) ||
    (tab === 'albums' && albums.length > 0) ||
    (tab === 'mixtapes' && mixtapes.length > 0) ||
    (tab === 'merch' && merch.length > 0) ||
    (tab === 'beats' && beats.length > 0);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="store-page">
      {/* Header */}
      <div className="border-b border-[#262626] bg-[#0A0A0A]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-12">
          <Link to="/" className="text-[#666] hover:text-white text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2" data-testid="store-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <h1 className="font-cabinet text-sm font-black uppercase tracking-[0.3em]">STORE</h1>
          <button onClick={() => setCartOpen(true)} className="relative text-[#A3A3A3] hover:text-white transition-colors" data-testid="store-cart-icon">
            <ShoppingCart className="w-5 h-5" />
            {totalItems > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-[#FF3B30] text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">{totalItems}</span>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        {/* Store Header */}
        <div className="text-center mb-10">
          <p className="overline text-[#DAA520] mb-2">OFFICIAL</p>
          <h2 className="font-cabinet text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-3" data-testid="store-heading">STORE</h2>
          <p className="text-[#A3A3A3] text-sm max-w-md mx-auto">Tracks, merch, and beats — all in one place. Every purchase directly supports an independent artist.</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-1 mb-10 border border-[#262626] inline-flex mx-auto w-fit" style={{ display: 'flex', justifyContent: 'center' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
                tab === t.id ? 'bg-[#FF3B30] text-white' : 'text-[#A3A3A3] hover:text-white hover:bg-[#141414]'
              }`}
              data-testid={`store-tab-${t.id}`}>
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" />
          </div>
        ) : !hasItems ? (
          <div className="text-center py-24">
            <ShoppingBag className="w-16 h-16 mx-auto text-[#262626] mb-4" />
            <p className="text-[#A3A3A3]">Nothing available for purchase right now. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-16">
            {/* Tracks Section */}
            {(tab === 'all' || tab === 'tracks') && tracks.length > 0 && (
              <section data-testid="store-tracks-section">
                {tab === 'all' && <h3 className="overline text-[#DAA520] mb-6">TRACKS</h3>}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {tracks.map(track => (
                    <div key={track.id} className="bg-[#141414] border border-[#262626] hover:border-[#FF3B30]/30 transition-colors group" data-testid={`store-track-${track.id}`}>
                      <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
                        {track.cover_image ? (
                          <img src={toFullUrl(track.cover_image)} alt={track.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Music className="w-12 h-12 text-[#262626]" /></div>
                        )}
                        {track.on_sale && track.sale_price != null && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#DAA520] text-black text-[10px] font-bold uppercase">SALE</span>
                        )}
                        {track.preview_url && (
                          <button onClick={() => playTrack(track)}
                            className="absolute bottom-2 right-2 w-10 h-10 bg-[#FF3B30] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`store-play-${track.id}`}>
                            {currentTrack?.id === track.id && isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                          </button>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="font-bold text-sm truncate">{track.title}</p>
                        <p className="text-xs text-[#A3A3A3] truncate">{track.artist}</p>
                        <div className="flex items-center justify-between mt-3">
                          {track.on_sale && track.sale_price != null ? (
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-xs text-[#A3A3A3] line-through">${track.price.toFixed(2)}</span>
                              <span className="font-mono font-bold text-[#DAA520]">${track.sale_price.toFixed(2)}</span>
                            </div>
                          ) : (
                            <span className="font-mono font-bold text-[#FF3B30]">${track.price.toFixed(2)}</span>
                          )}
                          <div className="flex items-center gap-1">
                            <button onClick={() => addItem({ item_type: 'track', item_id: track.id, title: track.title, price: getTrackPrice(track), cover_image: track.cover_image ? toFullUrl(track.cover_image) : null })}
                              className="w-8 h-8 flex items-center justify-center bg-[#262626] hover:bg-[#DAA520] hover:text-black transition-colors"
                              data-testid={`store-cart-track-${track.id}`}>
                              <ShoppingCart className="w-3.5 h-3.5" />
                            </button>
                            <Button size="sm" onClick={() => openPurchase(track, 'track')}
                              className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none text-xs h-8 px-3"
                              data-testid={`store-buy-track-${track.id}`}>
                              BUY
                            </Button>
                          </div>
                        </div>
                        <div className="flex justify-end mt-1">
                          <ShareButtons url={`${window.location.origin}/tracks`} title={track.title} type="track" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Mixtapes Section */}
            {(tab === 'all' || tab === 'mixtapes') && mixtapes.length > 0 && (
              <section data-testid="store-mixtapes-section">
                {tab === 'all' && <h3 className="overline text-[#22C55E] mb-6">MIXTAPES</h3>}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mixtapes.map(m => (
                    <div key={m.id} className="bg-[#141414] border border-[#262626] hover:border-[#22C55E]/40 transition-colors" data-testid={`store-mixtape-${m.id}`}>
                      <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
                        {m.cover_image ? (
                          <img src={toFullUrl(m.cover_image)} alt={m.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Music className="w-12 h-12 text-[#262626]" /></div>
                        )}
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#22C55E] text-black text-[10px] font-bold uppercase">MIXTAPE</span>
                      </div>
                      <div className="p-3">
                        <p className="font-bold text-sm truncate">{m.title}</p>
                        <p className="text-xs text-[#A3A3A3] truncate">{m.artist}</p>
                        {m.release_date && (
                          <p className="text-[10px] text-[#525252] mt-0.5">{m.release_date}</p>
                        )}
                        <div className="flex items-center justify-between mt-3">
                          <span className="font-mono font-bold text-[#22C55E]">${(m.price || 0).toFixed(2)}</span>
                          <Button size="sm" onClick={() => openPurchase(m, 'mixtape')}
                            className="bg-[#22C55E] hover:bg-white hover:text-[#0A0A0A] rounded-none text-xs h-8 px-3 text-black"
                            data-testid={`store-buy-mixtape-${m.id}`}>
                            BUY
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Albums Section — sell full albums as bundles */}
            {(tab === 'all' || tab === 'albums') && albums.length > 0 && (
              <section data-testid="store-albums-section">
                {tab === 'all' && <h3 className="overline text-[#DAA520] mb-6">ALBUMS</h3>}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {albums.map(album => {
                    const price = album.on_sale && album.sale_price != null ? album.sale_price : album.price;
                    return (
                      <div key={album.id} className="bg-[#141414] border border-[#262626] hover:border-[#22C55E]/40 transition-colors" data-testid={`store-album-${album.id}`}>
                        <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
                          {album.cover_image ? (
                            <img src={toFullUrl(album.cover_image)} alt={album.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Music className="w-12 h-12 text-[#262626]" /></div>
                          )}
                          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#22C55E] text-black text-[10px] font-bold uppercase">ALBUM</span>
                          {album.on_sale && album.sale_price != null && (
                            <span className="absolute top-2 right-2 px-2 py-0.5 bg-[#DAA520] text-black text-[10px] font-bold uppercase">SALE</span>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="font-bold text-sm truncate">{album.title}</p>
                          <p className="text-xs text-[#A3A3A3] truncate">{album.artist}</p>
                          <p className="text-[10px] text-[#525252] mt-0.5">{album.track_count || 0} track{album.track_count !== 1 ? 's' : ''}</p>
                          <div className="flex items-center justify-between mt-3">
                            {album.on_sale && album.sale_price != null ? (
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-xs text-[#A3A3A3] line-through">${album.price.toFixed(2)}</span>
                                <span className="font-mono font-bold text-[#DAA520]">${album.sale_price.toFixed(2)}</span>
                              </div>
                            ) : (
                              <span className="font-mono font-bold text-[#22C55E]">${(album.price || 0).toFixed(2)}</span>
                            )}
                            <Button size="sm" onClick={() => openPurchase(album, 'album')}
                              className="bg-[#22C55E] hover:bg-white hover:text-[#0A0A0A] rounded-none text-xs h-8 px-3 text-black"
                              data-testid={`store-buy-album-${album.id}`}>
                              BUY ALBUM
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Merch Section */}
            {(tab === 'all' || tab === 'merch') && merch.length > 0 && (
              <section data-testid="store-merch-section">
                {tab === 'all' && <h3 className="overline text-[#DAA520] mb-6">MERCH</h3>}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {merch.map(item => (
                    <div key={item.id} className="bg-[#141414] border border-[#262626] hover:border-[#FF3B30]/30 transition-colors group" data-testid={`store-merch-${item.id}`}>
                      <div className="aspect-square bg-[#0A0A0A] relative overflow-hidden">
                        {item.image ? (
                          <img src={toFullUrl(item.image)} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-12 h-12 text-[#262626]" /></div>
                        )}
                        {item.on_sale && item.sale_price != null && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#DAA520] text-black text-[10px] font-bold uppercase">SALE</span>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="font-bold text-sm truncate">{item.title}</p>
                        {item.gildan_style && <p className="text-[9px] text-[#DAA520] font-mono">{item.gildan_style}</p>}
                        <p className="text-xs text-[#A3A3A3] truncate">{item.description}</p>
                        {item.fabric_details && <p className="text-[9px] text-[#555] mt-1">{item.fabric_details}</p>}
                        {item.sizes?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.sizes.map(s => (
                              <span key={s} className="px-1 py-0.5 text-[8px] font-bold border border-[#333] text-[#888]">{s}</span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-3">
                          {item.on_sale && item.sale_price != null ? (
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-xs text-[#A3A3A3] line-through">${item.price.toFixed(2)}</span>
                              <span className="font-mono font-bold text-[#DAA520]">${item.sale_price.toFixed(2)}</span>
                            </div>
                          ) : (
                            <span className="font-mono font-bold text-[#FF3B30]">${item.price.toFixed(2)}</span>
                          )}
                          <div className="flex items-center gap-1">
                            <button onClick={() => addItem({ item_type: 'merch', item_id: item.id, title: item.title, price: getMerchPrice(item), cover_image: item.image ? toFullUrl(item.image) : null })}
                              className="w-8 h-8 flex items-center justify-center bg-[#262626] hover:bg-[#DAA520] hover:text-black transition-colors"
                              data-testid={`store-cart-merch-${item.id}`}>
                              <ShoppingCart className="w-3.5 h-3.5" />
                            </button>
                            <Button size="sm" onClick={() => openPurchase(item, 'merch')}
                              className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none text-xs h-8 px-3"
                              data-testid={`store-buy-merch-${item.id}`}>
                              BUY
                            </Button>
                          </div>
                        </div>
                        <div className="flex justify-end mt-1">
                          <ShareButtons url={`${window.location.origin}/store`} title={item.title} type="merch" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Beats Section */}
            {(tab === 'all' || tab === 'beats') && beats.length > 0 && (
              <section data-testid="store-beats-section">
                {tab === 'all' && <h3 className="overline text-[#DAA520] mb-6">BEATS</h3>}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {beats.map(beat => {
                    const isLeaseOnly = beat.lease_only;
                    return (
                      <div key={beat.id} className="bg-[#141414] border border-[#262626] p-4 hover:border-[#FF3B30]/30 transition-colors" data-testid={`store-beat-${beat.id}`}>
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-16 h-16 bg-[#0A0A0A] border border-[#262626] flex items-center justify-center flex-shrink-0">
                            {beat.cover_image ? (
                              <img src={toFullUrl(beat.cover_image)} alt={beat.title} className="w-full h-full object-cover" />
                            ) : (
                              <Headphones className="w-8 h-8 text-[#262626]" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold truncate">{beat.title}</p>
                            {beat.bpm && <p className="text-xs text-[#A3A3A3]">{beat.bpm} BPM{beat.key ? ` · ${beat.key}` : ''}</p>}
                          </div>
                        </div>
                        {beat.preview_url && (
                          <div className="flex gap-2 mb-3">
                            <Button onClick={() => playTrack({ ...beat, preview_url: beat.preview_url })} variant="outline"
                              className="flex-1 rounded-none border-[#262626] hover:border-[#FF3B30] hover:text-[#FF3B30] text-xs"
                              data-testid={`store-play-beat-${beat.id}`}>
                              {currentTrack?.id === beat.id && isPlaying ? <><Pause className="w-4 h-4 mr-2" /> PLAYING</> : <><Play className="w-4 h-4 mr-2" /> PREVIEW</>}
                            </Button>
                            <button onClick={() => { const ok = addToQueue({ ...beat, preview_url: beat.preview_url }); toast[ok ? 'success' : 'message'](ok ? `Added "${beat.title}" to queue` : 'Already in queue'); }}
                              className="h-9 w-9 flex items-center justify-center bg-[#141414] hover:bg-[#DAA520] hover:text-black border border-[#262626] transition-colors"
                              title="Add to queue" data-testid={`store-queue-beat-${beat.id}`}>
                              <ListPlus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          {beat.license_basic > 0 && (
                            <Button size="sm" onClick={() => { setSelectedLicense('basic'); openPurchase(beat, 'beat'); }}
                              className="flex-1 bg-[#262626] hover:bg-[#FF3B30] rounded-none text-xs h-9"
                              data-testid={`store-buy-beat-basic-${beat.id}`}>
                              Basic · {beat.on_sale && beat.sale_price_basic ? (
                                <span className="text-[#DAA520]">${beat.sale_price_basic}</span>
                              ) : `$${beat.license_basic}`}
                            </Button>
                          )}
                          {beat.license_premium > 0 && (
                            <Button size="sm" onClick={() => { setSelectedLicense('premium'); openPurchase(beat, 'beat'); }}
                              className="flex-1 bg-[#262626] hover:bg-[#FF3B30] rounded-none text-xs h-9"
                              data-testid={`store-buy-beat-premium-${beat.id}`}>
                              Premium · {beat.on_sale && beat.sale_price_premium ? (
                                <span className="text-[#DAA520]">${beat.sale_price_premium}</span>
                              ) : `$${beat.license_premium}`}
                            </Button>
                          )}
                          {beat.license_exclusive > 0 && !isLeaseOnly && (
                            <Button size="sm" onClick={() => { setSelectedLicense('exclusive'); openPurchase(beat, 'beat'); }}
                              className="flex-1 bg-[#DAA520] text-black hover:bg-white rounded-none text-xs h-9"
                              data-testid={`store-buy-beat-exclusive-${beat.id}`}>
                              Exclusive · {beat.on_sale && beat.sale_price_exclusive ? (
                                <span>${beat.sale_price_exclusive}</span>
                              ) : `$${beat.license_exclusive}`}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Purchase Dialog */}
      <Dialog open={purchaseOpen} onOpenChange={setPurchaseOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-md" aria-describedby="purchase-desc">
          <DialogHeader>
            <DialogTitle className="font-cabinet text-xl font-black uppercase">
              {purchaseType === 'beat' ? `LICENSE "${purchaseItem?.title}"` : `PURCHASE "${purchaseItem?.title}"`}
            </DialogTitle>
          </DialogHeader>
          <p id="purchase-desc" className="sr-only">Complete your purchase</p>
          <div className="mt-4 space-y-4">
            {purchaseItem?.cover_image && (
              <div className="aspect-video bg-[#262626] overflow-hidden">
                <img src={toFullUrl(purchaseItem.cover_image)} alt={purchaseItem.title} className="w-full h-full object-cover" />
              </div>
            )}

            {/* Size selector for merch */}
            {purchaseType === 'merch' && purchaseItem?.sizes?.length > 0 && (
              <div>
                <Label className="overline text-xs">Size</Label>
                <div className="flex gap-2 mt-2">
                  {purchaseItem.sizes.map(s => (
                    <button key={s} onClick={() => setSelectedSize(s)}
                      className={`px-3 py-1.5 text-xs font-bold border transition-colors ${selectedSize === s ? 'bg-[#FF3B30] border-[#FF3B30]' : 'border-[#262626] text-[#A3A3A3] hover:border-[#FF3B30]'}`}
                      data-testid={`size-${s}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {/* License selector for beats */}
            {purchaseType === 'beat' && (
              <div>
                <Label className="overline text-xs">License Type</Label>
                <div className="flex gap-2 mt-2">
                  {['basic', 'premium', 'exclusive'].map(tier => {
                    const price = purchaseItem?.[`license_${tier}`];
                    if (!price || price <= 0) return null;
                    if (tier === 'exclusive' && purchaseItem?.lease_only) return null;
                    return (
                      <button key={tier} onClick={() => setSelectedLicense(tier)}
                        className={`flex-1 px-3 py-2 text-xs font-bold border transition-colors uppercase ${selectedLicense === tier ? 'bg-[#FF3B30] border-[#FF3B30]' : 'border-[#262626] text-[#A3A3A3] hover:border-[#FF3B30]'}`}
                        data-testid={`license-${tier}`}>{tier}</button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="overline text-xs">Email (for delivery)</Label>
              <Input value={customerEmail} onChange={e => setCustomerEmail(e.target.value)}
                className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="your@email.com" type="email" required
                data-testid="store-purchase-email" />
            </div>

            {purchaseType === 'track' && (
              <div>
                <Label className="overline text-xs">Discount Code (optional)</Label>
                <div className="flex gap-2 mt-2">
                  <Input value={discountCode} onChange={e => setDiscountCode(e.target.value)}
                    className="bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="Enter code"
                    data-testid="store-discount-input" />
                  <Button onClick={validateDiscount} disabled={validatingDiscount} variant="outline"
                    className="rounded-none border-[#262626]" data-testid="store-apply-discount">
                    {validatingDiscount ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                  </Button>
                </div>
                {discountInfo && <p className="text-xs text-[#22C55E] mt-1">{discountInfo.discount_percent}% discount applied!</p>}
              </div>
            )}

            <div className="pt-2 border-t border-[#262626]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-[#A3A3A3]">Total</span>
                <span className="font-mono text-2xl font-bold text-[#DAA520]" data-testid="store-purchase-total">${getPurchasePrice().toFixed(2)}</span>
              </div>
              <Button onClick={handleCheckout} disabled={purchasing}
                className="w-full rounded-none h-12 text-base font-bold uppercase"
                style={{ background: '#00D166', color: '#000', boxShadow: '0 2px 16px rgba(0,209,102,0.35)' }}
                data-testid="store-checkout-btn">
                {purchasing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> PROCESSING...</> : `PAY $${getPurchasePrice().toFixed(2)}`}
              </Button>
              <p className="text-center text-[10px] text-[#666] mt-2">Secure payment via Stripe</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
